import { MAP_NAMES  } from 'entities/map';

import config from 'config';

import { EMPTY_OBJECT } from 'shared/constants';

import {
  handleClusterHover,
  getClusterParams
} from '../utils';

// @ts-ignore
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { MapName } from 'entities/map';
import { FitBounds } from 'common/types';
import {
  IMap,
  ContainerRef,
  MapParams,
  MapPadding,
  OnMapEventListener,
  OnChangeBounds,
  MapEventListener,
  OnChangeMapStreetView,
  MapType,
  CenterCoords,
  ClickListener
} from '../types';

export class GoogleMap implements IMap {
  name: MapName;
  overlay?: GoogleMapsOverlay;
  private _map?: google.maps.Map;
  private _containerRef: ContainerRef;
  private _clickListeners: ClickListener[] = [];

  constructor (containerRef: ContainerRef) {
    this._containerRef = containerRef;
    this.name = MAP_NAMES.google;
  }

  initMap(params?: MapParams) {
    const {
      center = { lat: 37.444585, lng: 65.607399 }
    } = params || {};

    if (this._containerRef && window.google) {
      this._map = new window.google.maps.Map(
        this._containerRef,
        {
          center,
          zoom: 4,
          fullscreenControl: false,
          streetViewControl: true,
          mapTypeControl: false,
          cameraControl: false,
          zoomControl: false,
          clickableIcons: false,
          mapId: config.mapId,
          gestureHandling: 'greedy',
          draggableCursor: 'inherit',
          keyboardShortcuts: false,
          streetViewControlOptions: {
            position:google.maps.ControlPosition.RIGHT_CENTER
          }
        }
      );

      this._initOverlay();
    }
  }

  private _initOverlay() {
    this.overlay = new GoogleMapsOverlay({
      useDevicePixels: 1.6,
      interleaved: false,
      onClick: (event: any) => {
        const { object, layer = {} } = event;
        let markerType: string | undefined;

        if (!object) {
          markerType = undefined;
        } else if (object && object.data) {
          markerType = 'marker';
        } else {
          markerType = 'cluster';
        }

        this._clickListeners.forEach(fn => {
          fn({
            markerType,
            layerName: layer?.id,
            data: (object || EMPTY_OBJECT).data
          })
        });

        if (markerType === 'cluster') {
          const clusterParams = getClusterParams(event);
          this._revealCluster(clusterParams); 
        }
      },
      onHover: (event: any) => {
        handleClusterHover(event, this._containerRef);
      },
      layers: [],
    });

    if (this._map) {
      this.overlay.setMap(this._map);
    }
  }

  private _revealCluster(params?: { lat: number; lng: number; expansionZoom: number}) {
    if (params) {
      const { lat, lng, expansionZoom } = params
      this.setZoom(expansionZoom + 1);
      this.setCenter({ lat, lng });
    }
  }

  getNativeMap(): google.maps.Map | undefined {
    return this._map;
  }

  fitBounds(value: FitBounds, padding?: MapPadding) {
    const latLngBounds = new google.maps.LatLngBounds(value.sw, value.ne);
    this._map?.fitBounds(latLngBounds, padding);
  }

  zoomIn() {
    if (this._map) {
      const currentZoom = this._map.getZoom() || 0;
      this._map.setZoom(currentZoom + 1);
    }
  }

  zoomOut() {
    if (this._map) {
      const currentZoom = this._map.getZoom() || 0;
      this._map.setZoom(currentZoom - 1);
    }
  }

  setZoom(value: number) {
    if (this._map) {
      this._map.setZoom(value);
    }
  }

  setCenter({ lat, lng }: CenterCoords = {}) {
    if (this._map && lat && lng) {
      this._map.panTo({ lat, lng });
    }
  }

  _getTopRightPos() {
    return {
      latitude: this._map?.getBounds()?.getNorthEast().lat(),
      longitude: this._map?.getBounds()?.getNorthEast().lng()
    };
  }

  _getBottomLeftPos() {
    return {
      latitude: this._map?.getBounds()?.getSouthWest().lat(),
      longitude:this._map?.getBounds()?.getSouthWest().lng()
    };
  }

  setMapType(mapType: MapType) {
    if (this._map) {
      this._map.setMapTypeId(mapType);
    }
  }

  addListenerDragStart(fn: OnMapEventListener): MapEventListener {
    return this._map?.addListener('dragstart', fn);
  }

  addClickListener(listener: ClickListener) {
    this._clickListeners.push(listener);
  }

  removeClickListener(listener: ClickListener) {
    this._clickListeners = this._clickListeners.filter(item => item !== listener);
  }

  addListenerBoundsChanged(fn: OnChangeBounds): MapEventListener {
    return this._map?.addListener('bounds_changed', () => fn({
      TopRight: this._getTopRightPos(),
      BottomLeft: this._getBottomLeftPos()
    }));
  }

  addListenerStreetViewChange(fn: OnChangeMapStreetView): MapEventListener {
    return this._map?.getStreetView()?.addListener('visible_changed', () => {
      const isStreetView = !!this._map?.getStreetView()?.getVisible();
      fn(isStreetView)
    });
  }
};
